import { supabase } from '../supabaseClient';
import { debug } from '../helpers/debug';
import { sanitizeInputByType } from '../helpers/formatHelpers';

const normalizeEmail = (email = '') => sanitizeInputByType(email, 'text', { maxLength: 320 }).trim().toLowerCase();
const isMissingProjectMembersTable = (err) => err?.code === 'PGRST205' || /project_members/i.test(err?.message || '');

const flattenProjectWithCount = (project = {}) => ({
  ...project,
  doc_count: project.doc_count?.[0]?.count || 0
});

async function attachProjectCollaborators(projects = [], currentUserId = null) {
  if (!Array.isArray(projects) || projects.length === 0) return projects;

  const projectIds = projects.map(project => project.id).filter(Boolean);
  if (projectIds.length === 0) return projects;

  try {
    const { data: members, error: membersError } = await supabase
      .from('project_members')
      .select('project_id, user_id, invited_email, invite_status, access_level, created_at')
      .in('project_id', projectIds)
      .neq('access_level', 'owner')
      .order('created_at', { ascending: true });

    if (membersError) {
      if (isMissingProjectMembersTable(membersError)) {
        return projects.map(project => ({ ...project, collaborators: [] }));
      }
      throw membersError;
    }

    const ownerIds = projects
      .map(project => project.owner_user_id)
      .filter(ownerId => ownerId && String(ownerId) !== String(currentUserId));

    const memberUserIds = (members || []).map(member => member.user_id).filter(Boolean);
    const userIds = [...new Set([...ownerIds, ...memberUserIds])];

    let usersById = new Map();
    if (userIds.length > 0) {
      const { data: users, error: usersError } = await supabase
        .from('users')
        .select('id, email, name')
        .in('id', userIds);

      if (usersError) {
        debug('[API] attachProjectCollaborators users lookup warning:', { type: 'warn' }, usersError);
      } else {
        usersById = new Map((users || []).map(user => [String(user.id), user]));
      }
    }

    // Batch-check for active public share links for these projects
    let activeShareProjectIds = new Set();
    try {
      const { data: shareLinks } = await supabase
        .from('share_links')
        .select('target_id, enabled, expires_at')
        .eq('link_type', 'project')
        .in('target_id', projectIds)
        .eq('enabled', true);

      const now = new Date();
      (shareLinks || []).forEach(sl => {
        if (!sl.expires_at || new Date(sl.expires_at) > now) {
          activeShareProjectIds.add(Number(sl.target_id));
        }
      });
    } catch {
      // non-critical — ignore
    }

    const membersByProject = new Map();
    (members || []).forEach(member => {
      const key = String(member.project_id);
      if (!membersByProject.has(key)) membersByProject.set(key, []);
      membersByProject.get(key).push(member);
    });

    return projects.map(project => {
      const collaborators = [];
      const seen = new Set();

      const ownerId = project.owner_user_id ? String(project.owner_user_id) : null;
      if (ownerId && ownerId !== String(currentUserId)) {
        const ownerUser = usersById.get(ownerId);
        const ownerEmail = ownerUser?.email || ownerId;
        collaborators.push({
          id: `owner-${ownerId}`,
          identifier: ownerId,
          email: ownerEmail,
          name: ownerUser?.name || ownerEmail || 'Project owner',
          invite_status: 'accepted'
        });
        seen.add(ownerId);
      }

      const projectMembers = membersByProject.get(String(project.id)) || [];
      for (const member of projectMembers) {
        const memberUserId = member.user_id ? String(member.user_id) : null;
        if (memberUserId && memberUserId === String(currentUserId)) continue;

        const user = memberUserId ? usersById.get(memberUserId) : null;
        const email = user?.email || member.invited_email || '';
        const identifier = memberUserId || email;
        if (!email || !identifier || seen.has(identifier)) continue;

        collaborators.push({
          id: `member-${project.id}-${identifier}`,
          identifier,
          email,
          name: user?.name || email,
          invite_status: member.invite_status
        });

        seen.add(identifier);
        if (collaborators.length >= 4) break;
      }

      return {
        ...project,
        collaborators,
        has_public_link: activeShareProjectIds.has(Number(project.id))
      };
    });
  } catch (err) {
    debug('[API] attachProjectCollaborators error:', { type: 'warn' }, err);
    return projects.map(project => ({ ...project, collaborators: [] }));
  }
}

async function claimPendingInvites(userId, userEmail) {
  const normalized = normalizeEmail(userEmail);
  if (!userId || !normalized) return;

  try {
    const { error } = await supabase
      .from('project_members')
      .update({
        user_id: userId,
        updated_at: new Date().toISOString()
      })
      .is('user_id', null)
      .eq('invite_status', 'invited')
      .ilike('invited_email', normalized);

    if (error) throw error;
  } catch (err) {
    if (isMissingProjectMembersTable(err)) return;
    debug('[API] claimPendingInvites error:', { type: 'warn' }, err);
  }
}

async function ensureMembershipRow(projectId, userId, fallbackLevel = 'editor') {
  if (!projectId || !userId) return;

  const now = new Date().toISOString();
  const { data: existing, error: existingError } = await supabase
    .from('project_members')
    .select('id, access_level')
    .eq('project_id', projectId)
    .eq('user_id', userId)
    .maybeSingle();

  if (existingError) throw existingError;
  if (existing) return existing;

  let accessLevel = fallbackLevel;
  try {
    const { data: project, error: projectError } = await supabase
      .from('projects')
      .select('owner_user_id')
      .eq('id', projectId)
      .maybeSingle();
    if (projectError) throw projectError;
    if (project && String(project.owner_user_id) === String(userId)) {
      accessLevel = 'owner';
    }
  } catch (err) {
    debug('[API] ensureMembershipRow owner lookup failed:', { type: 'warn' }, err);
  }

  const { data: inserted, error: insertError } = await supabase
    .from('project_members')
    .insert([{
      project_id: projectId,
      user_id: userId,
      invite_status: 'accepted',
      access_level: accessLevel,
      accepted_at: now,
      invited_by: userId
    }])
    .select('id, access_level')
    .single();

  if (insertError) throw insertError;
  return inserted;
}

export async function apiGetProjects(userId, userEmail = '') {
  try {
    if (!userId) return [];
    const normalizedEmail = normalizeEmail(userEmail);

    await claimPendingInvites(userId, userEmail);

    const { data: ownedProjects, error: ownedError } = await supabase
      .from('projects')
      .select(`
        *,
        doc_count:project_docs(count)
      `)
      .eq('owner_user_id', userId)
      .order('created_at', { ascending: true });

    if (ownedError) throw ownedError;

    const owned = (ownedProjects || []).map(project => ({
      ...flattenProjectWithCount(project),
      is_shared: false,
      access_level: 'owner'
    }));

    let sharedQuery = supabase
      .from('project_members')
      .select(`
        id,
        project_id,
        access_level,
        viewed_at,
        modified_at,
        invite_status,
        projects (
          *,
          doc_count:project_docs(count)
        )
      `)
      .in('invite_status', ['invited', 'accepted'])
      .order('created_at', { ascending: true });

    if (normalizedEmail) {
      sharedQuery = sharedQuery.or(`user_id.eq.${userId},invited_email.ilike.${normalizedEmail}`);
    } else {
      sharedQuery = sharedQuery.eq('user_id', userId);
    }

    const { data: sharedMemberships, error: sharedError } = await sharedQuery;

    if (sharedError) {
      if (isMissingProjectMembersTable(sharedError)) return owned;
      throw sharedError;
    }

    const ownedIds = new Set(owned.map(project => Number(project.id)));
    const shared = (sharedMemberships || [])
      .map(member => {
        const project = flattenProjectWithCount(member.projects || {});
        if (!project.id) return null;
        if (ownedIds.has(Number(project.id))) return null;

        return {
          ...project,
          is_shared: true,
          membership_id: member.id,
          access_level: member.access_level,
          invite_status: member.invite_status,
          viewed_at: member.viewed_at,
          modified_at: member.modified_at
        };
      })
      .filter(Boolean);

    const merged = [...owned, ...shared];
    return await attachProjectCollaborators(merged, userId);
  } catch (err) {
    debug('[API] getProjects error:', { type: 'error' }, err);
    return [];
  }
}

// Create a new project for a user
export async function apiCreateProject(_userId, name = 'New Project') {
  try {
    const requestName = sanitizeInputByType(name, 'title', { maxLength: 180 }) || 'New Project';
    const { data, error } = await supabase
      .rpc('create_project', { p_name: requestName });

    if (error) throw error;
    if (!data) throw new Error('create_project RPC returned no data');

    return { ...data, doc_count: data.doc_count ?? 0, is_shared: false, access_level: 'owner' };
  } catch (err) {
    debug('[API] createProject error:', { type: 'error' }, err);
    throw err;
  }
}


// Rename a project
export async function apiRenameProject(projectId, newName) {
  try {
    const safeName = sanitizeInputByType(newName, 'title', { maxLength: 180 }) || 'Untitled';
    const { data, error } = await supabase
      .rpc('rename_project', { p_project_id: projectId, p_name: safeName });

    if (error) throw error;
    return data;
  } catch (err) {
    debug('[renameProject] error:', { type: 'error' }, err);
    throw err;
  }
}

export async function apiGetProject(projectId) {
  try {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('id', projectId)
      .single();

    if (error) throw error;
    return data;
  } catch (err) {
    debug('[API] getProject error:', { type: 'error' }, err);
    return null;
  }
}

export async function apiDeleteProject(projectId) {
  try {
    const { error } = await supabase.rpc('delete_project', { p_project_id: projectId });

    if (error) throw error;
    return true;
  } catch (err) {
    debug('[API] deleteProject error:', { type: 'error' }, err);
    return false;
  }
}

export async function apiListProjectMembers(projectId) {
  try {
    const { data, error } = await supabase
      .from('project_members')
      .select(`
        id,
        project_id,
        user_id,
        invited_email,
        invite_status,
        access_level,
        viewed_at,
        modified_at,
        accepted_at,
        created_at
      `)
      .eq('project_id', projectId)
      .neq('access_level', 'owner')
      .order('created_at', { ascending: true });

    if (error) throw error;

    const userIds = [...new Set((data || []).map(member => member.user_id).filter(Boolean))];
    let usersById = new Map();

    if (userIds.length > 0) {
      const { data: users, error: usersError } = await supabase
        .from('users')
        .select('id, email, name')
        .in('id', userIds);

      if (usersError) throw usersError;
      usersById = new Map((users || []).map(user => [user.id, user]));
    }

    return (data || []).map(member => ({
      ...member,
      user: member.user_id ? usersById.get(member.user_id) || null : null,
      display_email: (member.user_id ? usersById.get(member.user_id)?.email : null) || member.invited_email || '',
      display_name: (member.user_id ? (usersById.get(member.user_id)?.name || usersById.get(member.user_id)?.email) : null) || member.invited_email || 'Unknown user'
    }));
  } catch (err) {
    debug('[API] listProjectMembers error:', { type: 'error' }, err);
    return [];
  }
}

export async function apiShareProjectWithEmail(projectId, email, invitedByUserId, defaultAccessLevel = 'editor') {
  const normalizedEmail = normalizeEmail(email);
  if (!projectId || !normalizedEmail) throw new Error('Missing projectId or email');

  try {
    const { data, error } = await supabase.rpc('share_project_with_email', {
      p_project_id: projectId,
      p_email: normalizedEmail,
      p_access_level: defaultAccessLevel
    });

    if (error) throw error;
    return data;
  } catch (err) {
    debug('[API] shareProjectWithEmail error:', { type: 'error' }, err);
    throw err;
  }
}

export async function apiRemoveProjectMember(projectId, membershipId) {
  try {
    const { error } = await supabase.rpc('remove_project_member', {
      p_project_id: projectId,
      p_membership_id: membershipId
    });

    if (error) throw error;
    return true;
  } catch (err) {
    debug('[API] removeProjectMember error:', { type: 'error' }, err);
    return false;
  }
}

export async function apiChangeMemberRole(membershipId, newRole) {
  try {
    const { error } = await supabase
      .from('project_members')
      .update({ access_level: newRole })
      .eq('id', membershipId);

    if (error) throw error;
    return true;
  } catch (err) {
    debug('[API] changeMemberRole error:', { type: 'error' }, err);
    return false;
  }
}

export async function apiLeaveProject(projectId, userId) {
  try {
    const { error } = await supabase.rpc('leave_project', { p_project_id: projectId });

    if (error) throw error;
    return true;
  } catch (err) {
    debug('[API] leaveProject error:', { type: 'error' }, err);
    return false;
  }
}

export async function apiTouchProjectViewed(projectId, userId) {
  try {
    await ensureMembershipRow(projectId, userId, 'editor');

    const now = new Date().toISOString();
    const { error } = await supabase
      .from('project_members')
      .update({ viewed_at: now, updated_at: now })
      .eq('project_id', projectId)
      .eq('user_id', userId)
      .in('invite_status', ['invited', 'accepted']);

    if (error) throw error;
    return true;
  } catch (err) {
    debug('[API] touchProjectViewed error:', { type: 'warn' }, err);
    return false;
  }
}

export async function apiAcceptProjectInvite(projectId, userId, userEmail = '') {
  try {
    const { error } = await supabase.rpc('accept_project_invite', { p_project_id: projectId });
    if (error) throw error;
    return true;
  } catch (err) {
    debug('[API] acceptProjectInvite error:', { type: 'warn' }, err);
    return false;
  }
}

export async function apiTouchProjectModified(projectId, userId) {
  try {
    await ensureMembershipRow(projectId, userId, 'editor');

    const now = new Date().toISOString();
    const { error } = await supabase
      .from('project_members')
      .update({ modified_at: now, updated_at: now })
      .eq('project_id', projectId)
      .eq('user_id', userId)
      .eq('invite_status', 'accepted');

    if (error) throw error;
    return true;
  } catch (err) {
    debug('[API] touchProjectModified error:', { type: 'warn' }, err);
    return false;
  }
}

// // src/helpers/projectHelpers.js
// export const renameProject = async (projectId, newName) => {
//   if (!projectId || !newName) return null;
//   try {
//     // Assuming you have an API function for renaming
//     const response = await fetch(`/api/projects/${projectId}`, {
//       method: 'PATCH',
//       headers: { 'Content-Type': 'application/json' },
//       body: JSON.stringify({ name: newName }),
//     });
//     return await response.json();
//   } catch (err) {
//     console.error('[projectHelpers] renameProject error:', err);
//     return null;
//   }
// };